/**
 * How an infographic shows, from the urls the admin filled in. `pdf`: the
 * in-app PDF renderer. `embed`: an iframe, for a Figma page (figma.com,
 * figma.site) or our own HTML published on juisci.xyz. `image`: a picture the
 * reader zooms. `none`: nothing to show.
 *
 * The embed url wins when it is one of those. Any other embed url (none set,
 * an invalid url, a webinar registration page) falls back to the uploaded
 * poster (`image`, the admin "Image" slot, documented as the actual uploaded
 * infographic), then to the card thumbnail as a last resort.
 */
export type InfographicSource =
  | { kind: "pdf"; url: string }
  | { kind: "embed"; url: string }
  | { kind: "image"; url: string }
  | { kind: "none" };

const IMAGE_EXTENSIONS = [".webp", ".png", ".jpg", ".jpeg", ".avif", ".gif"];

function embedSource(embedUrl: string): InfographicSource | null {
  if (!URL.canParse(embedUrl)) return null;
  const parsed = new URL(embedUrl);
  if (parsed.pathname.endsWith(".pdf")) return { kind: "pdf", url: embedUrl };
  if (
    parsed.hostname.endsWith(".figma.com") ||
    parsed.hostname.endsWith(".figma.site")
  ) {
    return { kind: "embed", url: embedUrl };
  }
  const path = parsed.pathname.toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext))) {
    return { kind: "image", url: embedUrl };
  }
  // Our own generated HTML, published through `/jup --public`: an HTML page
  // with no extension, like a figma.site page.
  if (parsed.hostname === "juisci.xyz") return { kind: "embed", url: embedUrl };
  return null;
}

export function infographicSource(infographic: {
  embedURL: string | null;
  image: { url: string } | null;
  thumbnail: { url: string } | null;
}): InfographicSource {
  const embed =
    infographic.embedURL === null ? null : embedSource(infographic.embedURL);
  if (embed !== null) return embed;
  const posterUrl =
    infographic.image !== null
      ? infographic.image.url
      : infographic.thumbnail !== null
        ? infographic.thumbnail.url
        : null;
  if (posterUrl === null) return { kind: "none" };
  if (posterUrl.toLowerCase().endsWith(".pdf")) {
    return { kind: "pdf", url: posterUrl };
  }
  return { kind: "image", url: posterUrl };
}
